import * as React from "react";
import { FaTrashAlt } from "react-icons/fa";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import * as actions from "../../../../actions/course-author/course-author";
import {
  ButtonRow,
  CheckboxDescription,
  DeleteButton,
  FooterNote,
  SettingsInputItem,
} from "../../../../App/Common/Components/FormComponents";
import { SectionTitle, WarningSection } from "../../../../App/Common/Styles/containers";
import { IState } from "../../../../App/reducer";
import { IOrganization } from "../../../../reducers/course-author/types";
import * as selectors from "../../../../selectors/selectors";
import { IFullCourse } from "../../../course-author/types";
import Checkbox from "../Checkbox";
import { Select } from "../FormElements";

interface IDangerZoneStateProps {
  administrableOrgIds: string[];
  administrableOrgs: IOrganization[];
  isOrgAdmin: boolean;
}

interface IDangerZoneDispatchProps {
  fetchAdministrableOrgs: (orgIds: string[]) => void;
}

export interface IDangerZoneOwnProps {
  course: IFullCourse;
  courseId: string;
  deleteCourse: () => void;
  modifyOrganizationOwner: (newOrgId: string) => void;
  toggleLockCourse: () => void;
}

type DangerZoneProps = IDangerZoneOwnProps & IDangerZoneStateProps & IDangerZoneDispatchProps;

const CONFIRMATION_MSG =
  "Are you sure you want to archive this course? To unarchive a course you need to speak to a Salt Administrator.";

const confirmationLockCourseMsg = (locked: boolean): string =>
  `Are you sure you want to ${locked ? "unlock" : "lock"} the course?`;

function deleteCourse(props: IDangerZoneOwnProps): () => void {
  return () => {
    if (confirm(CONFIRMATION_MSG)) {
      props.deleteCourse();
    }
  };
}

const Note = styled(FooterNote)`
  display: block;
`;

export const DangerZone: React.FC<DangerZoneProps> = props => {
  const { course, isOrgAdmin, administrableOrgs } = props;
  const [customChecked, setCustomChecked] = React.useState(course.state.locked);
  const toggleUseCustomCompledtedMessage = () => {
    if (confirm(confirmationLockCourseMsg(course.state.locked))) {
      setCustomChecked(!customChecked);
      props.toggleLockCourse();
    }
  };
  const modifyOrganizationOwner = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const newOrgId = ev.target.value;
    if (course.state.owner !== newOrgId) {
      props.modifyOrganizationOwner(newOrgId);
    }
  };

  React.useEffect(() => {
    if (isOrgAdmin) {
      props.fetchAdministrableOrgs(props.administrableOrgIds);
    }
  }, [props.courseId]);

  return (
    <>
      <SectionTitle>Danger Zone</SectionTitle>
      {isOrgAdmin && (
        <WarningSection>
          <div>
            <h1>Owning Organization</h1>
            <Select onChange={modifyOrganizationOwner}>
              {administrableOrgs.map(({ displayName, organizationId }, i) => (
                <option key={i} value={organizationId} selected={course.state.owner === organizationId}>
                  {displayName}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <SettingsInputItem>
              <label>
                <Checkbox
                  customChecked={customChecked}
                  defaultChecked={course.state.locked}
                  onChange={toggleUseCustomCompledtedMessage}
                />
                <CheckboxDescription>Lock course</CheckboxDescription>
              </label>
            </SettingsInputItem>
            <Note>
              Locking the course prevents making accidental changes once it has been rolled out. All patches to the
              course will fail, until the course has been unlocked.
            </Note>
          </div>
        </WarningSection>
      )}
      <ButtonRow margin={2} width={13}>
        <DeleteButton onClick={deleteCourse(props)}>
          <span>
            <FaTrashAlt /> Archive Course
          </span>
        </DeleteButton>
      </ButtonRow>
    </>
  );
};

const mapStateToProps = (state: IState): IDangerZoneStateProps => ({
  administrableOrgIds: state.App._.administrableOrgIds,
  administrableOrgs: state.App.CourseAuthor._.administrableOrgs,
  isOrgAdmin: selectors.isOrgAdmin(state),
});

const mapDispatchToProps = (dispatch: Dispatch): IDangerZoneDispatchProps => ({
  fetchAdministrableOrgs: (orgIds: string[]) => dispatch(actions.fetchAdministableOrgs(orgIds)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DangerZone);
